import React, { useState, useEffect } from "react";
import Drawer from "react-modern-drawer";
import { useMediaQuery } from "../../../../../hooks/useMediaQuery";
import ButtonIcon from "../../../../../components/ButtonIcon/ButtonIcon";
import Button from "../../../../../components/new-button";
import StatusDot from "../../../../../components/StatusDot/StatusDot";
import Checkbox from "../../../../../components/Checkbox/Index";
import TagInput from "../../../../../components/form/tag-input";
import Select from "../../../../../components/form/select";
import CloseIcon24 from "../../../../../assets/svg/icons/common/CloseIcon24";
import style from "./FilterDrawer.module.scss";
import "react-modern-drawer/dist/index.css";
import {
  progression_statuses,
  sales_left_progression_statuses,
} from "../../../../../../constants";
import CalendarIcon from "../../../../../assets/svg/icons/common/Calender";
import ArrowDown from "../../../../../assets/svg/icons/common/ArrowDown";
import useApplicationStore from "../../../../../stores/application";
import useCommonStore from "../../../../../stores/common";
import useUserStore from "../../../../../stores/user";

// Import API functions
import {
  getEmsRepCodes,
  GetAllOrganizationsSingleCall,
  GetOrganizationById,
  getBanks,
  getEmsOffices,
  getUserBranchesList,
} from "../../../../../../api";
import { useSavedFilters } from "../../../../../hooks/useSavedFilters";
import { usePaginatedOfficeOptions } from "../../../../../hooks/usePaginatedOfficeOptions";
import SavedFiltersSection from "../../../../../components/SavedFiltersSection/SavedFiltersSection";
import { isApplicationsFilterSnapshotEmpty } from "../../../../../utils/savedFilterSnapshotEmpty";

const SAVED_FILTER_PAGE = "applications";

/**
 * FilterDrawer component for filtering applications
 * @component
 * @returns {JSX.Element} FilterDrawer component
 */
function FilterDrawer() {
  const filterDrawerOpen = useCommonStore((state) => state.filterDrawerOpen);
  const setFilterDrawerOpen = useCommonStore(
    (state) => state.setFilterDrawerOpen,
  );
  const setAppFilterSearchQuery = useApplicationStore(
    (state) => state.setAppFilterSearchQuery,
  );

  const appFilterSearchQuery = useApplicationStore(
    (state) => state.appFilterSearchQuery,
  );

  const relationshipManagers = useApplicationStore(
    (state) => state.relationshipManagers,
  );
  const isPCAdmin = useUserStore((state) => state.isPCAdmin);
  const user = useUserStore((state) => state.user);

  const [progressions, setProgressions] = useState(
    appFilterSearchQuery.progression_id || [],
  );
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [errors, setErrors] = useState({ createdFrom: "", createdTo: "" });
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [showAllSalesStatuses, setShowAllSalesStatuses] = useState(false);
  const [showAllRepCodes, setShowAllRepCodes] = useState(false);
  const [repCodes, setRepCodes] = useState([]);
  const [selectedRepCodes, setSelectedRepCodes] = useState([]);
  const [isLoadingRepCodes, setIsLoadingRepCodes] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState(
    appFilterSearchQuery.organization_id?.value ??
      appFilterSearchQuery.organization_id ??
      "",
  );
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(
    appFilterSearchQuery.user_id || [],
  );
  const [banks, setBanks] = useState([]);
  const [selectedBanks, setSelectedBanks] = useState(
    appFilterSearchQuery.bank || [],
  );
  const [progress, setProgress] = useState(
    appFilterSearchQuery.left_progression_filter || [],
  );
  const [selectedManagers, setSelectedManagers] = useState(
    appFilterSearchQuery.manager_id,
  );
  const [selectedUnderwriters, setSelectedUnderwriters] = useState(
    appFilterSearchQuery.uw_ids,
  );
  const [selectedChannel, setSelectedChannel] = useState(
    appFilterSearchQuery.channel || null,
  );
  const isMobile = useMediaQuery(768);
  const isSmallMobile = useMediaQuery(420);
  const [selectedEmsOffices, setSelectedEmsOffices] = useState([]);
  const {
    options: emsOffices,
    hasMore: hasMoreEmsOffices,
    loading: isLoadingEmsOffices,
    loadingMore: isLoadingMoreEmsOffices,
    search: searchEmsOffices,
    loadMore: loadMoreEmsOffices,
  } = usePaginatedOfficeOptions(getEmsOffices, { lazy: true });

  // EMS Branch filter
  const [emsBranches, setEmsBranches] = useState([]);
  const [selectedEmsBranches, setSelectedEmsBranches] = useState([]);
  const [isLoadingEmsBranches, setIsLoadingEmsBranches] = useState(false);

  useEffect(() => {
    if (filterDrawerOpen) {
      const today = new Date().toISOString().split("T")[0];
      const from = appFilterSearchQuery.created_from ?? "";
      const to = appFilterSearchQuery.created_to ?? "";
      setCreatedFrom(from && from <= today ? from : "");
      setCreatedTo(to && to <= today ? to : "");
      setErrors({ createdFrom: "", createdTo: "" });
      setProgressions(appFilterSearchQuery.progression_id || []);
      setSelectedRepCodes(appFilterSearchQuery.rep_codes || []);
      setSelectedManagers(appFilterSearchQuery.manager_id || []);
      setSelectedUnderwriters(appFilterSearchQuery.uw_ids || []);
      setSelectedUsers(appFilterSearchQuery.user_id || []);
      setSelectedBanks(appFilterSearchQuery.bank || []);
      setProgress(appFilterSearchQuery.left_progression_filter || []);
      setSelectedChannel(appFilterSearchQuery.channel || null);
      setSelectedEmsOffices(appFilterSearchQuery.ems_office || []);
      setSelectedEmsBranches(appFilterSearchQuery.ems_branch || []);
      setSelectedOrganizations(
        appFilterSearchQuery.organization_id?.value ??
          appFilterSearchQuery.organization_id ??
          "",
      );
      fetchRepCodes();
      fetchOrganizations();
      fetchBanks();
      fetchEmsBranches();
      searchEmsOffices("");
    }
  }, [filterDrawerOpen]);

  useEffect(() => {
    if (user?.channel == "sales_channel" && user?.role == "admin") {
      if (user?.organization_id) {
        fetchOrganizationUsers(user?.organization_id);
      }
    }
  }, [user]);

  const fetchRepCodes = async () => {
    try {
      setIsLoadingRepCodes(true);
      const response = await getEmsRepCodes();

      if (Array.isArray(response)) {
        setRepCodes(response);
      } else if (response.data && Array.isArray(response.data)) {
        setRepCodes(response.data);
      } else {
        setRepCodes([]);
      }
    } catch (error) {
      console.error("Failed to fetch rep codes:", error);
      setRepCodes([]);
    } finally {
      setIsLoadingRepCodes(false);
    }
  };

  const fetchEmsBranches = async () => {
    try {
      setIsLoadingEmsBranches(true);
      const response = await getUserBranchesList();
      if (response.status) {
        setEmsBranches(response.data);
      }
    } catch (error) {
      setEmsBranches([]);
    } finally {
      setIsLoadingEmsBranches(false);
    }
  };

  /**
   * Fetches banks from the API
   * @async
   * @returns {Promise<void>}
   */
  const fetchBanks = async () => {
    try {
      const response = await getBanks();

      if (response.status && response.data) {
        const bankOptions = response.data.map((bank) => ({
          label: bank.name,
          value: bank.id,
        }));
        // Remove the first bank (usually default/placeholder)
        bankOptions.shift();
        setBanks(bankOptions);
      } else {
        setBanks([]);
      }
    } catch (error) {
      console.error("Failed to fetch banks:", error);
      setBanks([]);
    }
  };

  /**
   * Fetches organizations from the API
   * @async
   * @returns {Promise<void>}
   */
  const fetchOrganizations = async () => {
    try {
      const response = await GetAllOrganizationsSingleCall();
      if (response.organization && response.organization.length > 0) {
        const orgOptions = response.organization.map((org) => ({
          label: org.name,
          value: org.id,
        }));
        setOrganizations(orgOptions);
      }
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
      setOrganizations([]);
    }
  };

  /**
   * Fetches users for a specific organization
   * @async
   * @param {string} orgId - The organization ID
   * @returns {Promise<void>}
   */
  const fetchOrganizationUsers = async (orgId) => {
    if (!orgId) {
      setOrganizationUsers([]);
      setSelectedUsers([]);
      return;
    }

    try {
      const response = await GetOrganizationById(orgId);

      if (response.success && response.users && response.users.length > 0) {
        const userOptions = response.users.map((user) => ({
          label: user.name,
          value: user.id,
        }));
        setOrganizationUsers(userOptions);
      } else {
        setOrganizationUsers([]);
      }
    } catch (error) {
      console.error("Failed to fetch organization users:", error);
      setOrganizationUsers([]);
    }
  };

  /**
   * Checks if there are any validation errors
   * @returns {boolean} True if there are errors, false otherwise
   */
  const hasErrors = () => {
    return Boolean(errors.createdFrom || errors.createdTo);
  };

  /**
   * Handles progression status checkbox changes
   * @param {Object} event - The change event
   * @param {boolean} event.checked - Whether the checkbox is checked
   * @param {string} event.value - The value of the checkbox
   */
  const onProgressionChange = (event) => {
    const { checked, value } = event.target;

    setProgressions((prevProgressions) =>
      checked
        ? [...prevProgressions, +value]
        : prevProgressions.filter((item) => +item !== +value),
    );
  };

  /**
   * Handles representative code checkbox changes
   * @param {Object} event - The change event
   * @param {boolean} event.checked - Whether the checkbox is checked
   * @param {string} event.value - The value of the checkbox
   */
  const onRepCodeChange = (event) => {
    const { checked, value } = event.target;

    setSelectedRepCodes((prevRepCodes) =>
      checked
        ? [...prevRepCodes, value]
        : prevRepCodes.filter((code) => code !== value),
    );
  };

  /**
   * Handles date input changes and validates dates
   * @param {Object} e - The change event
   * @param {string} type - The type of date field ('from' or 'to')
   */
  const onDateChange = (e, type) => {
    const value = e.target.value;
    let newErrors = { ...errors };
    const today = new Date().toISOString().split("T")[0];

    // Check if selected date is in the future
    if (value > today) {
      newErrors[type === "from" ? "createdFrom" : "createdTo"] =
        "Future dates are not allowed.";
      if (type === "from") {
        setCreatedFrom(value);
      } else {
        setCreatedTo(value);
      }
      setErrors(newErrors);
      return;
    }

    if (type === "from") {
      if (createdTo && value > createdTo) {
        newErrors.createdFrom = "FROM date cannot be later than TO date.";
      } else {
        newErrors.createdFrom = "";
      }
      setCreatedFrom(value);
    } else {
      if (createdFrom && value < createdFrom) {
        newErrors.createdTo = "To date cannot be earlier than FROM date.";
      } else {
        newErrors.createdTo = "";
      }
      setCreatedTo(value);
    }
    setErrors(newErrors);
  };

  const getCurrentFilterValues = () => ({
    left_progression_filter: progress,
    manager_id: selectedManagers || [],
    uw_ids: selectedUnderwriters || [],
    progression_id: progressions || [],
    user_id: selectedUsers || [],
    organization_id: selectedOrganizations || "",
    bank: selectedBanks || [],
    created_from: createdFrom,
    created_to: createdTo,
    rep_codes: selectedRepCodes || [],
    channel: selectedChannel || null,
    ems_office: selectedEmsOffices || [],
    ems_branch: selectedEmsBranches || [],
  });

  const applyFilterValues = (filters) => {
    setProgress(filters.left_progression_filter || []);
    setSelectedManagers(filters.manager_id || []);
    setSelectedUnderwriters(filters.uw_ids || []);
    setProgressions(filters.progression_id || []);
    setSelectedUsers(filters.user_id || []);
    setSelectedOrganizations(filters.organization_id || "");
    setSelectedBanks(filters.bank || []);
    setCreatedFrom(filters.created_from || "");
    setCreatedTo(filters.created_to || "");
    setSelectedRepCodes(filters.rep_codes || []);
    setSelectedChannel(filters.channel || null);
    setSelectedEmsOffices(filters.ems_office || []);
    setSelectedEmsBranches(filters.ems_branch || []);

    if (filters.organization_id) {
      fetchOrganizationUsers(filters.organization_id);
    }
  };

  const getAppliedSnapshot = () => {
    const q = useApplicationStore.getState().appFilterSearchQuery;
    return {
      left_progression_filter: [...(q.left_progression_filter || [])],
      manager_id: [...(q.manager_id || [])],
      uw_ids: [...(q.uw_ids || [])],
      progression_id: [...(q.progression_id || [])],
      user_id: [...(q.user_id || [])],
      organization_id: q.organization_id ?? "",
      bank: [...(q.bank || [])],
      created_from: q.created_from ?? "",
      created_to: q.created_to ?? "",
      rep_codes: [...(q.rep_codes || [])],
      channel: q.channel ?? null,
      ems_office: [...(q.ems_office || [])],
      ems_branch: [...(q.ems_branch || [])],
    };
  };

  const savedFiltersApi = useSavedFilters({
    page: SAVED_FILTER_PAGE,
    drawerOpen: filterDrawerOpen,
    getFilterSnapshot: getCurrentFilterValues,
    applyFilterSnapshot: applyFilterValues,
    isSnapshotEmpty: isApplicationsFilterSnapshotEmpty,
    getAppliedSnapshot,
  });

  /**
   * Applies the selected filters
   */
  const onApplyClick = () => {
    setAppFilterSearchQuery({
      left_progression_filter: progress,
      search: appFilterSearchQuery.search || "",
      sortBy: appFilterSearchQuery.sortBy || "id",
      sortValue: appFilterSearchQuery.sortValue || "DESC",
      manager_id: selectedManagers || [],
      uw_ids: selectedUnderwriters || [],
      progression_id: progressions || [],
      page: 1,
      user_id: selectedUsers || [],
      organization_id: selectedOrganizations || "",
      bank: selectedBanks || [],
      created_from: createdFrom,
      created_to: createdTo,
      rep_codes: selectedRepCodes || [],
      channel: selectedChannel || null,
      ems_office: selectedEmsOffices || [],
      ems_branch: selectedEmsBranches || [],
    });
    savedFiltersApi.commitAppliedSavedFilter();
    setFilterDrawerOpen(false);
  };

  /**
   * Clears all filters
   */
  const clearFiler = () => {
    setAppFilterSearchQuery({
      left_progression_filter: [],
      search: "",
      sortBy: "id",
      sortValue: "DESC",
      manager_id: [],
      uw_ids: [],
      progression_id: [],
      page: 1,
      user_id: [],
      organization_id: "",
      bank: [],
      created_from: "",
      created_to: "",
      rep_codes: [],
      channel: null,
      ems_office: [],
      ems_branch: [],
    });
    setSelectedRepCodes([]);
    setSelectedOrganizations([]);
    setSelectedUsers([]);
    setOrganizationUsers([]);
    setSelectedBanks([]);
    setProgress([]);
    setSelectedManagers([]);
    setSelectedUnderwriters([]);
    setCreatedFrom("");
    setCreatedTo("");
    setFilterDrawerOpen(false);
    setSelectedEmsBranches([]);
    setSelectedEmsOffices([]);
  };

  return (
    <Drawer
      open={filterDrawerOpen}
      onClose={() => setFilterDrawerOpen(false)}
      direction={isMobile ? "left" : "right"}
      className={style.filter_drawer}
      size={isSmallMobile ? "320px" : isMobile ? "375px" : "440px"}
      zIndex={8888}
      overlayColor={"#000"}
      overlayOpacity={0.5}
    >
      <div className={style.drawer_container}>
        <div className={style.drawer_title_block}>
          <div className={style.head}>
            <ButtonIcon onClick={() => setFilterDrawerOpen(false)}>
              <CloseIcon24 />
            </ButtonIcon>
          </div>

          <div className={style.header}>
            <h5>Filter</h5>
          </div>
        </div>

        <div className={style.content}>
          <SavedFiltersSection
            savedFilterOptions={savedFiltersApi.savedFilterOptions}
            selectedSavedFilter={savedFiltersApi.selectedSavedFilter}
            onSavedFilterChange={savedFiltersApi.handleSavedFilterSelect}
            onDeleteSavedFilter={savedFiltersApi.handleDeleteSavedFilter}
            isDeletingFilter={savedFiltersApi.isDeletingFilter}
            editFilterName={savedFiltersApi.editFilterName}
            onEditFilterNameChange={savedFiltersApi.setEditFilterName}
            onUpdateSavedFilter={savedFiltersApi.handleUpdateSavedFilter}
            isUpdatingFilter={savedFiltersApi.isUpdatingFilter}
            showSaveFilterInput={savedFiltersApi.showSaveFilterInput}
            onShowSaveInput={savedFiltersApi.openSaveFilterInput}
            newFilterName={savedFiltersApi.newFilterName}
            onNewFilterNameChange={savedFiltersApi.setNewFilterName}
            onSaveFilter={savedFiltersApi.handleSaveFilter}
            isSavingFilter={savedFiltersApi.isSavingFilter}
            onCancelNewFilter={() => {
              savedFiltersApi.cancelSaveFilterInput();
              savedFiltersApi.setNewFilterName("");
            }}
          />

          <div className={style.filterContainer}>
            {user?.channel == "partner_channel" && (
              <>
                <TagInput
                  id={"team_lead"}
                  name={"team_lead"}
                  label={"Team Lead"}
                  options={relationshipManagers}
                  value={relationshipManagers.filter((option) =>
                    selectedManagers.includes(option.value),
                  )}
                  onChange={(value) => {
                    const newManagerIds = value.map((option) => option.value);
                    setSelectedManagers(newManagerIds);
                  }}
                  placeholder={"Select Team Lead"}
                />

                <TagInput
                  id={"underwriter"}
                  name={"underwriter"}
                  label={"Underwriter"}
                  options={relationshipManagers}
                  value={relationshipManagers.filter((option) =>
                    selectedUnderwriters.includes(option.value),
                  )}
                  onChange={(value) => {
                    const newUnderwriterIds = value.map(
                      (option) => option.value,
                    );
                    setSelectedUnderwriters(newUnderwriterIds);
                  }}
                  placeholder={"Select Underwriter"}
                />
              </>
            )}

            {isPCAdmin && (
              <>
                <Select
                  id={"organization"}
                  name={"organization"}
                  label={"Organization"}
                  options={organizations}
                  value={selectedOrganizations}
                  onChange={(value) => {
                    setSelectedOrganizations(value?.value ?? value ?? "");
                    fetchOrganizationUsers(value?.value ?? value ?? "");
                  }}
                  placeholder={"Select Organization"}
                  search={true}
                />

                <TagInput
                  id={"user"}
                  name={"user"}
                  label={"User"}
                  options={organizationUsers}
                  value={organizationUsers.filter((option) =>
                    selectedUsers.includes(option.value),
                  )}
                  onChange={(value) => {
                    setSelectedUsers(value.map((o) => o.value));
                  }}
                  placeholder={"Select Users"}
                />

                <TagInput
                  id={"bank"}
                  name={"bank"}
                  label={"Bank"}
                  options={banks}
                  value={banks.filter((option) =>
                    selectedBanks.includes(option.value),
                  )}
                  onChange={(value) => {
                    setSelectedBanks(value.map((o) => o.value));
                  }}
                  placeholder={"Select Banks"}
                />
              </>
            )}

            {user?.role == "admin" && !isPCAdmin && (
              <TagInput
                id={"user"}
                name={"user"}
                label={"User"}
                options={organizationUsers}
                value={organizationUsers.filter((option) =>
                  selectedUsers.includes(option.value),
                )}
                onChange={(value) => {
                  const newUserIds = value.map((option) => option.value);
                  setSelectedUsers(newUserIds);
                }}
                placeholder={"Select Users"}
              />
            )}
          </div>
          <div className={style.status_filter}>
            <h5 className={style.heading}>STATUS</h5>
            <div className={style.filter_list}>
              {sales_left_progression_statuses
                .slice(
                  0,
                  showAllSalesStatuses
                    ? sales_left_progression_statuses.length
                    : 5,
                )
                .map((status) => (
                  <div className={style.filter_list_item} key={status.value}>
                    <Checkbox
                      name={"left_progression_filter"}
                      id={status.value}
                      value={status.value}
                      checked={progress.includes(status.value)}
                      onChange={(e) => {
                        const { checked, value } = e.target;
                        setProgress((prev) =>
                          checked
                            ? [...prev, value]
                            : prev.filter((item) => item !== value),
                        );
                      }}
                    />

                    <div
                      onClick={() => {
                        setProgress((prev) =>
                          prev.includes(status.value)
                            ? prev.filter((item) => item !== status.value)
                            : [...prev, status.value],
                        );
                      }}
                    >
                      <StatusDot color={`${status.color}`} />
                      <span>{status.name}</span>
                    </div>
                  </div>
                ))}
            </div>
            {sales_left_progression_statuses.length > 5 && (
              <div className={style.more_wrapper}>
                <button
                  className={style.more_button}
                  onClick={() => setShowAllSalesStatuses(!showAllSalesStatuses)}
                >
                  <div
                    className={style.icon_wrapper}
                    style={{
                      transform: showAllSalesStatuses
                        ? "rotate(180deg)"
                        : "none",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    <ArrowDown />
                  </div>
                  <span>{showAllSalesStatuses ? "Less" : "More"}</span>
                </button>
              </div>
            )}
          </div>

          <div className={style.date_filter}>
            <h5 className={style.heading}>CREATED</h5>

            <div className={style.date_fields}>
              <div className={style.date_field}>
                <span className={style.icon}>
                  <CalendarIcon />
                </span>
                <input
                  type="date"
                  name="created_from"
                  value={createdFrom}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => onDateChange(e, "from")}
                  aria-label="Created from date"
                />
              </div>

              <div className={style.date_field}>
                <span className={style.icon}>
                  <CalendarIcon />
                </span>
                <input
                  type="date"
                  name="created_to"
                  value={createdTo}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => onDateChange(e, "to")}
                  aria-label="Created to date"
                />
              </div>
            </div>
            {errors.createdFrom && (
              <p className={style.error}>{errors.createdFrom}</p>
            )}
            {errors.createdTo && (
              <p className={style.error}>{errors.createdTo}</p>
            )}
          </div>

          <div className={style.status_filter}>
            <h5 className={style.heading}>KURV OFFICE</h5>
            {/* Always mounted: swapping in a loading message on an empty search remounted it and lost the typed term. */}
            <div
              className={style.filterContainer}
              style={{ padding: "8px 24px" }}
            >
              <TagInput
                id={"ems_office"}
                name={"ems_office"}
                options={emsOffices}
                // office_id is its own label here, so no lookup needed.
                value={selectedEmsOffices.map((id) => ({
                  label: id,
                  value: id,
                }))}
                onChange={(value) => {
                  const newEmsOffices = value.map((option) => option.value);
                  setSelectedEmsOffices(newEmsOffices);
                }}
                placeholder={"Select Kurv Office"}
                search={true}
                async
                serverFiltered
                loading={isLoadingEmsOffices}
                onSearchChange={searchEmsOffices}
                hasMore={hasMoreEmsOffices}
                loadingMore={isLoadingMoreEmsOffices}
                onLoadMore={loadMoreEmsOffices}
              />
            </div>
          </div>

          <div className={style.status_filter}>
            <h5 className={style.heading}>KURV BRANCH</h5>
            {isLoadingEmsBranches ? (
              <div className={style.loading}>Loading Kurv branches...</div>
            ) : (
              <div
                className={style.filterContainer}
                style={{ padding: "8px 24px" }}
              >
                <TagInput
                  id={"ems_branch"}
                  name={"ems_branch"}
                  options={emsBranches}
                  value={emsBranches?.filter((option) =>
                    selectedEmsBranches.includes(option.value),
                  )}
                  onChange={(value) => {
                    const newEmsBranches = value.map((option) => option.value);
                    setSelectedEmsBranches(newEmsBranches);
                  }}
                  placeholder={"Select Kurv Branch"}
                  search={true}
                />
              </div>
            )}
          </div>

          <div className={style.status_filter}>
            <h5 className={style.heading}>REP CODE</h5>
            {isLoadingRepCodes ? (
              <div className={style.loading}>Loading rep codes...</div>
            ) : (
              <div
                className={style.filterContainer}
                style={{ padding: "8px 24px" }}
              >
                <TagInput
                  id={"rep_code"}
                  name={"rep_code"}
                  options={repCodes}
                  value={repCodes.filter((option) =>
                    selectedRepCodes.includes(option.value),
                  )}
                  onChange={(value) => {
                    const newRepCodes = value.map((option) => option.value);
                    setSelectedRepCodes(newRepCodes);
                  }}
                  placeholder={"Select Rep Code"}
                  search={true}
                />
              </div>
            )}
          </div>
        </div>

        <div className={style.footer}>
          <Button variant="secondary" size="large" onClick={clearFiler}>
            Clear
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={onApplyClick}
            disabled={hasErrors()}
          >
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

export default FilterDrawer;
